import {
  keepPreviousData,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';
import { PAGINATION_SORT_DIRECTION } from '~/constants/pagination';

import { KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import { queryKeysDataExchange } from './queryKeys';
import {
  toSupplierOverviewRequest,
  zodSupplierOverviewFilters,
  zodSupplierOverviewOrderBy,
  zodSupplierOverviewPagination,
} from './supplierOverviewRequest';

const zodDataExchangeQueryParams = zodSupplierOverviewFilters
  .extend({
    hasResponsiblePerson: z.boolean().optional().prefault(false),
    limit: z.number().gte(1).optional().prefault(2000), // PAGINATION_PAGE_SIZE_DEFAULT
    offset: z.number().gte(0).optional().prefault(0),
    orderBy: zodSupplierOverviewOrderBy.optional().prefault('alphabetically'),
    sortOrder: z.enum(PAGINATION_SORT_DIRECTION).optional().prefault('ASC'),
  })
  .refine(
    (data) => {
      if (data.searchText) {
        return data.sortOrder !== undefined;
      }

      return true;
    },
    {
      path: ['sortOrder'],
      error: 'sortOrder is required when searchText is provided',
    },
  )
  .refine(
    (data) => {
      if (data.offset || data.limit) {
        return data.offset >= 0 && data.limit >= 0;
      }

      return true;
    },
    {
      path: ['offset'],
      error: 'offset is required when limit is provided and vice versa',
    },
  );

export type DataExchangeQueryParams = z.infer<
  typeof zodDataExchangeQueryParams
>;

export type DataExchangeItem = Record<string, any>;

type DataExchangeResponse = {
  items: readonly DataExchangeItem[];
  nextLink: string;
};

/**
 * Generates the query options for the data exchanges query so they can be shared between the useQuery hook and the prefetching.
 */
export const getDataExchangesQueryOptions = ({
  queryParams,
  options,
}: {
  queryParams: Partial<DataExchangeQueryParams>;
  options?: Omit<UseQueryOptions<DataExchangeResponse>, 'queryKey' | 'queryFn'>;
}) => {
  const validatedQueryParameters = zodDataExchangeQueryParams.parse(
    withoutObjectKeysWhereValueIs(queryParams, [undefined, '']),
  );

  return {
    queryFn: async ({ signal }) =>
      fetchDataExchangesSuppliers(validatedQueryParameters, { signal }),
    queryKey: queryKeysDataExchange.getAllSuppliers(validatedQueryParameters),
    staleTime: ms('60s'),
    ...options,
  };
};

/**
 * Fetches all data exchange companies (suppliers) from the API.
 * The filters are sent in the POST body to avoid URL length limits; sorting and paging stay in the query string.
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/query_supplier_overview_data_exchange_company_information_query_post
 */
export const fetchDataExchangesSuppliers = async (
  queryParams: DataExchangeQueryParams,
  options?: KyRequestOptions,
) => {
  const { json, searchParams } = toSupplierOverviewRequest(queryParams);

  const response = await vestigasApi
    .post(ENDPOINT.DATA_EXCHANGE.GET_COMPANIES_ALL(), {
      json,
      searchParams,
      validate: {
        body: zodSupplierOverviewFilters,
        searchParams: zodSupplierOverviewPagination,
      },
      ...options,
    })
    .json<DataExchangeResponse>();

  return (
    response ?? {
      items: [],
      nextLink: '',
    }
  );
};

/**
 * React Query based custom hook for getting the data for all data exchange companies (suppliers) with given query parameters.
 */
export const useQueryDataExchangesSuppliers = (
  queryParams: Partial<DataExchangeQueryParams>,
  options?: Omit<UseQueryOptions<DataExchangeResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...getDataExchangesQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
};
